import React, { useEffect, useState } from "react";

const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || "")
  .trim()
  .replace(/\/+$/, "");

function parseContentDispositionFilename(header: string | null): string | null {
  if (!header) return null;
  const star = header.match(/filename\*=([^;]*)/i);
  if (star?.[1]) {
    const part = star[1].trim();
    const idx = part.indexOf("''");
    const value = idx >= 0 ? decodeURIComponent(part.slice(idx + 2)) : part;
    return value.replace(/^["']|["']$/g, "").trim() || null;
  }
  const plain = header.match(/filename="([^"]+)"/i) ?? header.match(/filename=([^;]*)/i);
  if (plain?.[1]) {
    return plain[1].replace(/^["']|["']$/g, "").trim() || null;
  }
  return null;
}

function buildFallbackFilename(url: string, audioOnly: boolean): string {
  const ext = audioOnly ? "mp3" : "mp4";
  const stamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
  try {
    const parsed = new URL(url.trim());
    const slug =
      parsed.pathname.split("/").filter(Boolean).pop() ||
      parsed.hostname.replace(/\./g, "-");
    const safe = slug.replace(/[^\w.-]+/g, "_").slice(0, 80) || "media";
    return `${safe}-${stamp}.${ext}`;
  } catch {
    return `media-${stamp}.${ext}`;
  }
}

const SUPPORTED_SITES = [
  { name: "YouTube", examples: "youtube.com, youtu.be" },
  { name: "Facebook", examples: "facebook.com, fb.watch" },
  { name: "Instagram", examples: "instagram.com/reel, /p/, /tv/" },
  { name: "TikTok", examples: "tiktok.com, vm.tiktok.com" },
  { name: "X (Twitter)", examples: "twitter.com, x.com" },
  { name: "Vimeo", examples: "vimeo.com" },
  { name: "Reddit", examples: "reddit.com, v.redd.it" },
  { name: "Twitch", examples: "clips.twitch.tv, twitch.tv" },
  { name: "Dailymotion", examples: "dailymotion.com" },
  { name: "Pinterest", examples: "pinterest.com/pin" },
  { name: "LinkedIn", examples: "linkedin.com/posts" },
  { name: "Snapchat", examples: "snapchat.com spotlight" },
  { name: "LinkedIn", examples: "linkedin.com/posts" },
  { name: "SoundCloud", examples: "soundcloud.com" },
  { name: "Kwai", examples: "kwai.com" },
] as const;

const App: React.FC = () => {
  const [url, setUrl] = useState("");
  const [message, setMessage] = useState("");
  const [isError, setIsError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [sitesOpen, setSitesOpen] = useState(false);
  const [audioOnly, setAudioOnly] = useState(false);

  useEffect(() => {
    if (!sitesOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setSitesOpen(false);
    };
    document.addEventListener("keydown", onKeyDown);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = "";
    };
  }, [sitesOpen]);

  const handleDownload = async () => {
    const trimmed = url.trim();
    if (!trimmed) {
      setIsError(true);
      setMessage("Please enter a URL");
      return;
    }
    setMessage("");
    setIsError(false);
    setLoading(true);
    try {
      const params = new URLSearchParams({ url: trimmed });
      if (audioOnly) params.set("audio_only", "true");
      const endpoint = API_BASE_URL
        ? `${API_BASE_URL}/api/download?${params}`
        : `/api/download?${params}`;
      const response = await fetch(endpoint);
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        setIsError(true);
        setMessage(err.detail || "Download failed");
        return;
      }
      const blob = await response.blob();
      const cd = response.headers.get("content-disposition");
      const suggested = response.headers.get("x-filename");
      let filename =
        parseContentDispositionFilename(cd) ||
        suggested?.trim() ||
        buildFallbackFilename(trimmed, audioOnly);
      if (!filename.includes(".")) filename += audioOnly ? ".mp3" : ".mp4";

      const a = document.createElement("a");
      a.href = window.URL.createObjectURL(blob);
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(a.href);
      setUrl("");
      setIsError(false);
      setMessage("Download successful!");
    } catch (e: unknown) {
      setIsError(true);
      setMessage(e instanceof Error ? e.message : "Download failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#ffa266] to-[#553434] flex items-center justify-center p-4">
      <div className="bg-white rounded-md shadow-lg p-8 w-full max-w-lg">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-700 mb-7 text-center truncate">
          💾 DownloadVid 💾
        </h1>
        <p className="text-sm text-gray-500 mb-4 text-center">
          Built for speed. Designed for simplicity. Trusted for reliability.
        </p>
        <p className="text-sm text-center mb-7">
          <button
            type="button"
            onClick={() => setSitesOpen(true)}
            className="text-[#594e95] font-medium underline underline-offset-2 hover:text-[#6c5eb8] focus:outline-none focus:ring-2 focus:ring-[#6c5eb8] focus:ring-offset-2 rounded"
          >
            See supported sites
          </button>
        </p>
        <div className="space-y-6">
          <div>
            <label
              htmlFor="url"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Enter video URL
            </label>
            <input
              id="url"
              type="text"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://www.instagram.com/reel..."
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#6c5eb8] focus:border-transparent outline-none"
              disabled={loading}
              autoComplete="off"
              autoFocus
            />
          </div>
          <div className="flex items-center justify-between gap-4 py-1">
            <span className="text-sm font-medium text-gray-700">
              Download mode
            </span>
            <div
              className="inline-flex rounded-md border border-gray-200 p-0.5 bg-gray-50"
              role="group"
              aria-label="Download mode"
            >
              <button
                type="button"
                onClick={() => setAudioOnly(false)}
                disabled={loading}
                className={`px-3 py-1.5 text-sm rounded transition-colors ${
                  !audioOnly
                    ? "bg-[#594e95] text-white shadow-sm"
                    : "text-gray-600 hover:text-gray-800"
                }`}
                aria-pressed={!audioOnly}
              >
                Video
              </button>
              <button
                type="button"
                onClick={() => setAudioOnly(true)}
                disabled={loading}
                className={`px-3 py-1.5 text-sm rounded transition-colors ${
                  audioOnly
                    ? "bg-[#594e95] text-white shadow-sm"
                    : "text-gray-600 hover:text-gray-800"
                }`}
                aria-pressed={audioOnly}
              >
                Audio only
              </button>
            </div>
          </div>
          <button
            onClick={handleDownload}
            disabled={loading}
            className="w-full bg-[#594e95] text-white py-2 px-4 rounded-md hover:bg-[#6c5eb8] focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-50 transition-colors"
          >
            {loading ? "Downloading..." : "Download"}
          </button>
          {message && (
            <div
              className={`p-3 rounded-md text-sm ${
                isError
                  ? "bg-red-50 text-red-700"
                  : "bg-green-50 text-green-700"
              }`}
            >
              {message}
            </div>
          )}
        </div>
      </div>

      {sitesOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
          role="presentation"
          onClick={() => setSitesOpen(false)}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="supported-sites-title"
            className="bg-white rounded-md shadow-xl w-full max-w-md max-h-[85vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-3 p-5 border-b border-gray-100">
              <div>
                <h2
                  id="supported-sites-title"
                  className="text-lg font-bold text-gray-800"
                >
                  Supported sites
                </h2>
                <p className="text-xs text-gray-500 mt-1">
                  Paste a public video link from any site below. Many more sites
                  work via our extractor.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setSitesOpen(false)}
                className="shrink-0 text-gray-400 hover:text-gray-600 text-2xl leading-none p-1"
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <ul className="overflow-y-auto p-5 space-y-3">
              {SUPPORTED_SITES.map((site) => (
                <li
                  key={site.name}
                  className="flex flex-col sm:flex-row sm:items-baseline sm:justify-between gap-0.5 py-2 border-b border-gray-50 last:border-0"
                >
                  <span className="font-medium text-gray-800">{site.name}</span>
                  <span className="text-xs text-gray-500 sm:text-right">
                    {site.examples}
                  </span>
                </li>
              ))}
            </ul>
            <div className="px-5 pb-5 pt-4 text-xs text-gray-500 space-y-1 border-t border-gray-100 mt-auto">
              <p>
                <strong className="text-gray-600">Limits:</strong> Video: MP4 up
                to 1080p. Audio: MP3. Max 60&nbsp;MB, daily cap per user, no
                playlists.
              </p>
              <p>
                Some sources (e.g. private or login-only videos) may fail even
                if listed here.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default App;
